// org scoping handled by RLS + project_id filter
import { supabase } from '@/lib/supabase';
import { SeoAgentRun } from '@/types/api/seo';

function mapRow(row: any): SeoAgentRun {
  return {
    id: row.id,
    organizationId: row.organization_id,
    agentId: row.agent_id,
    startedAt: row.started_at,
    finishedAt: row.finished_at,
    status: row.status,
    pagesScanned: row.pages_scanned,
    pagesChanged: row.pages_changed,
    tasksCreated: row.tasks_created,
    changesApplied: row.changes_applied,
    changesShadowed: row.changes_shadowed,
    tokensUsed: row.tokens_used,
    costUsd: Number(row.cost_usd ?? 0),
    errorMessage: row.error_message,
    summary: row.summary,
    createdAt: row.created_at,
  };
}

const seoAgentRunsService = {
  async getByAgent(agentId: string, limit = 20): Promise<SeoAgentRun[]> {
    const { data, error } = await supabase
      .from('seo_agent_runs')
      .select('*')
      .eq('agent_id', agentId)
      .order('started_at', { ascending: false })
      .limit(limit);

    if (error) throw error;
    return (data ?? []).map(mapRow);
  },

  async getByProject(projectId: string, limit = 50): Promise<SeoAgentRun[]> {
    // org scoping via RLS
    const query = supabase
      .from('seo_agent_runs')
      .select('*, seo_agents!inner(project_id)')
      .eq('seo_agents.project_id', projectId)
      .order('started_at', { ascending: false })
      .limit(limit);

    // org scoping via RLS

    const { data, error } = await query;
    if (error) throw error;
    return (data ?? []).map(mapRow);
  },

  async getRecentStats(projectId: string): Promise<{
    totalRuns: number;
    totalChanges: number;
    totalTokens: number;
    totalCost: number;
    errorRate: number;
  }> {
    // org scoping via RLS
    // Last 30 days of runs
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

    const query = supabase
      .from('seo_agent_runs')
      .select(
        'status, changes_applied, changes_shadowed, tokens_used, cost_usd, seo_agents!inner(project_id)'
      )
      .eq('seo_agents.project_id', projectId)
      .gte('started_at', thirtyDaysAgo.toISOString());

    // org scoping via RLS

    const { data, error } = await query;
    if (error) throw error;

    const runs = data ?? [];
    const totalRuns = runs.length;
    const totalChanges = runs.reduce(
      (s, r) => s + (r.changes_applied ?? 0) + (r.changes_shadowed ?? 0),
      0
    );
    const totalTokens = runs.reduce((s, r) => s + (r.tokens_used ?? 0), 0);
    const totalCost = runs.reduce((s, r) => s + Number(r.cost_usd ?? 0), 0);
    const errors = runs.filter((r) => r.status === 'error').length;
    const errorRate = totalRuns > 0 ? Math.round((errors / totalRuns) * 100) : 0;

    return { totalRuns, totalChanges, totalTokens, totalCost, errorRate };
  },
};

export default seoAgentRunsService;
